import React, { useState } from 'react';
import { UserSettingsDto } from '../../types/settings';
import { updateUserSettings } from '../../api/settingsApi';
import { Save } from 'lucide-react';
import { BackgroundPreview } from '../BackgroundPreview';

interface GeneralSettingsProps {
  userSettings: UserSettingsDto;
  onSettingsUpdate: () => void;
}

export const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  userSettings,
  onSettingsUpdate
}) => {
  const [settings, setSettings] = useState(userSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Check if theme or background has changed
      const themeChanged = settings.theme !== userSettings.theme;
      const backgroundChanged = settings.backgroundImage !== userSettings.backgroundImage;
      
      await updateUserSettings(settings);
      setSaveMessage('Settings saved successfully!');
      onSettingsUpdate();
      
      // Force browser refresh if theme or background changed to apply new styles
      if (themeChanged || backgroundChanged) {
        setTimeout(() => {
          window.location.reload();
        }, 500); // Small delay to show success message first
      } else {
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch {
      setSaveMessage('Failed to save settings. Please try again.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const updateSetting = (key: keyof UserSettingsDto, value: unknown) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">General Settings</h2>
        <p className="settings-section-description">
          Configure your theme and general preferences
        </p>
      </div>

      {/* UI/UX Settings */}
      <div className="settings-group">
        <h3 className="settings-group-title">Appearance</h3>
        
        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">Theme</div>
            <div className="settings-row-description">Choose your preferred color scheme</div>
          </div>
          <div className="settings-row-control">
            <select
              className="settings-select"
              value={settings.theme}
              onChange={(e) => updateSetting('theme', e.target.value)}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto (System)</option>
            </select>
          </div>
        </div>

        <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div className="settings-row-info" style={{ marginBottom: '12px' }}>
            <div className="settings-row-label">Background Image</div>
            <div className="settings-row-description">Choose a background pattern for the app</div>
          </div>
          <div style={{ width: '100%' }}>
            <BackgroundPreview
              value={settings.backgroundImage || 'none'}
              onChange={(value) => updateSetting('backgroundImage', value)}
            />
          </div>
        </div>

      </div>


      {/* Save Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="settings-button primary"
          style={{ minWidth: '120px' }}
        >
          {isSaving ? (
            'Saving...'
          ) : (
            <>
              <Save size={16} style={{ marginRight: '8px' }} />
              Save Changes
            </>
          )}
        </button>
        
        {saveMessage && (
          <span style={{ 
            color: saveMessage.includes('success') ? '#10b981' : '#ef4444',
            fontSize: '14px'
          }}>
            {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
};
